import * as Sentry from '@sentry/react'
import { consoleLoggingIntegration } from '@sentry/react'

import { scrubSensitiveData } from './scrubbing'

export interface SentryConfig {
  dsn: string
  environment: 'production' | 'development' | 'staging'
  release?: string
  enabled?: boolean
  tracesSampleRate?: number
}

/**
 * Initialize Sentry with error monitoring and performance tracking
 */
export function initSentry(config: SentryConfig): void {
  const {
    dsn,
    environment,
    release,
    enabled = environment === 'production',
    tracesSampleRate = environment === 'production' ? 0.1 : 1.0,
  } = config

  // Don't initialize if disabled or no DSN
  if (!enabled || !dsn) {
    // eslint-disable-next-line no-console
    console.log('[Sentry] Disabled or no DSN provided')

    return
  }

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      // Capture console errors and warnings as logs
      consoleLoggingIntegration({
        levels: ['error', 'warn'], // Only capture errors and warnings, not log messages
      }),
    ],
    // Performance monitoring - browser tracing is automatically enabled
    tracesSampleRate,
    // Trace propagation targets for distributed tracing
    tracePropagationTargets: ['localhost', /^https:\/\/.*\.cloud\.qse\.group/],
    // Capture 100% of transactions with errors
    tracesSampler: samplingContext => {
      // Always sample errors

      if (samplingContext.transactionContext?.tags?.error === 'true') {
        return 1.0
      }

      return tracesSampleRate
    },
    // Data scrubbing to prevent PII leakage
    beforeSend(event, _hint) {
      return scrubSensitiveData(event) as Sentry.ErrorEvent | null
    },
    // Don't send default PII
    sendDefaultPii: false,
    // Breadcrumb configuration
    maxBreadcrumbs: 50,
    beforeBreadcrumb(breadcrumb) {
      // Don't capture breadcrumbs with sensitive data
      if (breadcrumb.data) {
        const dataStr = JSON.stringify(breadcrumb.data).toLowerCase()
        const sensitivePatterns = ['password', 'token', 'secret', 'email', 'phone', 'credit', 'ssn']

        if (sensitivePatterns.some(pattern => dataStr.includes(pattern))) {
          return null
        }
      }

      return breadcrumb
    },
    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
      // Network errors that are not actionable
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      // Chrome extension errors
      'chrome-extension://',
      'moz-extension://',
    ],
  })

  // eslint-disable-next-line no-console
  console.log(`[Sentry] Initialized for environment: ${environment}`)
}
